import React, { ReactNode } from "react";
import styled from "styled-components";
import {
  AppStoreCTA,
  FlexColumnContainer,
  GooglePlayCTA,
} from "@trailhead/core-ui";
import { Link, colors } from "@trailhead/asurion-ui";
import { trackEvent } from "@trailhead/analytics";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { breakpoints, Text } from "@trailhead/asurion-ui";

import AppCTAImagePNG from "../../assets/AppCTAImageRightCropped.png";
import { PossibleOS, checkOS } from "../../utils";

const AppCTAContainer = styled.div`
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: ${colors.neutralBrightest};
  border: 1px solid ${colors.neutralBright};

  ${breakpoints.md2} {
    max-height: 18.75rem;
    justify-content: space-between;
    flex-direction: row;
  }
`;

const CallToAction = styled(FlexColumnContainer)`
  align-items: left;
  padding: 2rem 1rem;

  ${breakpoints.sm} {
    padding: 2rem;
  }

  ${breakpoints.md} {
    max-width: 25rem;
  }
`;

const CallToActionHeader = styled(Text)`
  font-size: 2rem;

  ${breakpoints.md} {
    font-size: 2.5rem;
  }
`;

const CallToActionSubHeader = styled(Text)`
  text-align: left;
  margin-top: 15px;
`;

const ImageWrapper = styled.div`
  padding: 0;
  text-align: center;
  border-radius: 0.5rem;
  overflow: hidden;

  & > img {
    height: 100%;
    max-height: 200px;

    ${breakpoints.md2} {
      max-height: unset;
    }

  }

  ${breakpoints.md2} {
    text-align: right;
  }
`;

const StoreCTAWrapper = styled.div`
  display: flex;
  margin-top: 1rem;
  cursor: pointer;
  gap: 0.5rem;
`;

const CTALink = styled(Link)`
  display: flex;
  text-decoration: none;
`;

const appStoreCTA = (
  <CTALink
    onClick={() => trackEvent("AppleCTALinkClick")}
    href="https://apps.apple.com/us/app/tech-coach/id1065341814"
    target="_blank"
    rel="noopener noreferrer"
  >
    <AppStoreCTA />
  </CTALink>
);

const googlePlayCTA = (
  <CTALink
    onClick={() => trackEvent("AndroidCTALinkClick")}
    href="https://play.google.com/store/apps/details?id=com.asurion.solutohome.verizon&hl=en_US&gl=US"
    target="_blank"
    rel="noopener noreferrer"
  >
    <GooglePlayCTA />
  </CTALink>
);

const appStoreAndGooglePlayCTA = [appStoreCTA, googlePlayCTA];
const StoreCTAs: Record<PossibleOS, ReactNode> = {
  iOS: appStoreCTA,
  android: googlePlayCTA,
  mac: appStoreCTA,
  windows: appStoreAndGooglePlayCTA,
  linux: appStoreAndGooglePlayCTA,
  unknown: appStoreAndGooglePlayCTA,
};

type Props = {
  osOverride?: PossibleOS;
}

export const AppCTAVersionB: React.FC<Props> = ({ osOverride }) => {
  const t = useSanitizedTranslation();

  return (
    <AppCTAContainer>
      <CallToAction>
        <CallToActionHeader size={6}>
          {t("Get help anytime, anywhere")}
        </CallToActionHeader>
        <CallToActionSubHeader>
          {t(
            "24/7 access to Tech Coach experts who are here to help optimize your device.",
          )}
        </CallToActionSubHeader>
        <StoreCTAWrapper>{StoreCTAs[osOverride || checkOS()]}</StoreCTAWrapper>
      </CallToAction>
      <ImageWrapper>
        <img src={AppCTAImagePNG} />
      </ImageWrapper>
    </AppCTAContainer>
  );
};
